import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV4,
  RadioButtonGroupMapping,
  RadioButtonsV2,
} from '@labrav/react-components'
import React, { useCallback, useMemo } from 'react'
import { faFileContract } from '@fortawesome/pro-regular-svg-icons'
import { FormikProps } from 'formik'
import { Typography } from '@mui/material'
import { get, isEmpty, isNil } from 'lodash'
import {
  DefaultNowType,
  useAccountId,
  FileUploadWithLoader,
} from '@labrav/common'

import { EulaType } from '../../../types/privateOffer'
import { bucketName } from '../../utils/constants'
import { useStyles } from './EulaSection.styles'

export interface EulaSectionProps {
  defaultNow: DefaultNowType
  formik: FormikProps<Record<string, unknown>>
  isMultiFileUpload?: boolean
  fileUploadDescription: string
  fileUploadButtonText: string
  fileUploadContainerTitle: string
  eulaOptionsHeading: string
  isOptional?: boolean
  quantity?: number
  eulaOptionSubHeading?: string
  showEulaFileUpload?: boolean
  disableEulaFileUpload?: boolean
  showEulaOptions?: boolean
  disableEulaOptions?: boolean
  eulaOptions?: RadioButtonGroupMapping[]
  fileSize: number
  updateErr?: (error: string) => void
  uploadError?: string
  eulaDescription?: JSX.Element
  eulaTypeKeyName?: string
  eulaFilesKeyName?: string
  uploadFileAssetType?: string
  uploadFolderName?: string
  disabled?: boolean
  hideSubHeading?: boolean
  acceptedFormats?: string[]
  isOptionalSection?: boolean
  sectionHeading?: string
  showHelpIcon?: boolean
  secondaryActions?: JSX.Element
}

export const EulaSection = ({
  isOptionalSection = false,
  sectionHeading = 'Configure End-user license agreement (EULA)',
  showHelpIcon = true,
  secondaryActions,
  ...restProps
}: EulaSectionProps) => {
  return (
    <BoxV4
      heading={sectionHeading}
      subHeading={
        restProps?.hideSubHeading
          ? ''
          : "At the point of purchase, buyers must agree to the terms and conditions outlined in your product's end-user license agreement (EULA)."
      }
      headingIcon={
        <FontAwesomeIcon
          icon={faFileContract}
          style={{ width: '11px', height: '14px' }}
        />
      }
      isOptional={isOptionalSection}
      secondaryActions={
        showHelpIcon && secondaryActions ? secondaryActions : undefined
      }
    >
      <EulaSectionContent {...restProps} />
    </BoxV4>
  )
}

export const EulaSectionContent = ({
  defaultNow,
  formik,
  isMultiFileUpload = false,
  fileUploadDescription,
  fileUploadButtonText,
  fileUploadContainerTitle,
  eulaOptionsHeading,
  isOptional = false,
  quantity = 1,
  eulaOptionSubHeading,
  showEulaFileUpload = false,
  disableEulaFileUpload = false,
  showEulaOptions = true,
  eulaOptions,
  disableEulaOptions = false,
  fileSize,
  updateErr,
  uploadError,
  eulaDescription,
  eulaTypeKeyName = 'eulaType',
  eulaFilesKeyName = 'eulaFile',
  uploadFileAssetType = 'eula_files',
  uploadFolderName = 'product_eula_files',
  acceptedFormats,
}: EulaSectionProps) => {
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const { values, setFieldValue, errors, touched } = formik
  const { classes } = useStyles()
  const handleEulaChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as EulaType
      const allowEmpty = eulaTypeKeyName === 'resellerLicenseAgreementType'

      if (isEmpty(value)) {
        if (allowEmpty) {
          // explicitly allow empty selection
          await setFieldValue(eulaTypeKeyName, '')
          await setFieldValue(eulaFilesKeyName, isMultiFileUpload ? [] : '')
        } else {
          // revert to previous value
          await setFieldValue(eulaTypeKeyName, get(values, eulaTypeKeyName))
        }
      } else {
        await setFieldValue(eulaTypeKeyName, value)
        await setFieldValue(eulaFilesKeyName, isMultiFileUpload ? [] : '')
        if (updateErr) {
          updateErr('')
        }
      }
    },
    [
      eulaTypeKeyName,
      eulaFilesKeyName,
      isMultiFileUpload,
      setFieldValue,
      values,
      updateErr,
    ]
  )

  const updateEulaFileUrl = useCallback(
    (fileUrl: string, index?: number) => {
      if (isMultiFileUpload && !isNil(index)) {
        const formikEulaValues = [
          ...((get(values, eulaFilesKeyName) as string[]) || []),
        ]
        formikEulaValues[index] = fileUrl
        setFieldValue(eulaFilesKeyName, formikEulaValues)
      } else {
        setFieldValue(eulaFilesKeyName, fileUrl)
      }
    },
    [isMultiFileUpload, values, eulaFilesKeyName, setFieldValue]
  )

  const deleteEulaFileUrl = useCallback(
    (index: number) => {
      if (isMultiFileUpload) {
        const selFiles = [
          ...((get(values, eulaFilesKeyName) as string[]) || []),
        ]
        selFiles.splice(index, 1)
        setFieldValue(eulaFilesKeyName, selFiles)
      } else {
        setFieldValue(eulaFilesKeyName, '')
      }
    },
    [isMultiFileUpload, values, eulaFilesKeyName, setFieldValue]
  )

  const existingFileLinksValues = useMemo(() => {
    if (isEmpty(get(values, eulaFilesKeyName))) {
      return []
    }
    if (!isMultiFileUpload) {
      return [get(values, eulaFilesKeyName) as string]
    }
    return [...(get(values, eulaFilesKeyName) as string[])] as string[]
  }, [values, eulaFilesKeyName, isMultiFileUpload])
  return (
    <div className={classes.eulaWrapper}>
      {showEulaOptions && (
        <div className={classes.sectionContainer} data-testid="product-eula">
          <Typography className={classes.sectionHeading}>
            Select EULA configuration option
          </Typography>
          {eulaDescription && eulaDescription}

          <div className={classes.radioButton}>
            <RadioButtonsV2
              name={eulaTypeKeyName}
              checkedValue={{
                key: get(values, eulaTypeKeyName) as string,
                value: get(values, eulaTypeKeyName) as string,
              }}
              handleChange={handleEulaChange}
              radioButtonMappings={(
                eulaOptions as RadioButtonGroupMapping[]
              ).map(option => ({
                ...option,
                disabled: disableEulaOptions,
              }))}
              gapBetween={'16px'}
              vertical
            />
          </div>
          {!!get(touched, eulaTypeKeyName) && get(errors, eulaTypeKeyName) && (
            <div className={classes.errorMessage}>
              {get(errors, eulaTypeKeyName)}
            </div>
          )}
        </div>
      )}
      {showEulaFileUpload ? (
        <div className={classes.sectionContainer} data-testid="file-uploader">
          {eulaOptionsHeading && (
            <div className={classes.textContainer}>
              <div className={classes.sectionHeadingContainer}>
                <Typography className={classes.sectionHeading}>
                  {eulaOptionsHeading}
                </Typography>
                {isOptional ? (
                  <Typography className={classes.sectionText}>
                    Optional
                  </Typography>
                ) : (
                  <></>
                )}
              </div>
              {eulaOptionSubHeading && (
                <Typography className={classes.sectionText}>
                  {eulaOptionSubHeading}
                </Typography>
              )}
            </div>
          )}
          <FileUploadWithLoader
            key={`eula-upload-${eulaFilesKeyName}`}
            keyName={`eula-upload-${eulaFilesKeyName}`}
            partnerId={accountId}
            existingFileLinks={existingFileLinksValues}
            assetType={uploadFileAssetType}
            folder={uploadFolderName}
            fileUploadDescription={fileUploadDescription}
            quantity={quantity}
            fileUploadType="txt/pdf"
            unit="MB"
            uploadFileSize={fileSize}
            acceptedFormats={acceptedFormats || ['.pdf', '.txt']}
            updateUrl={updateEulaFileUrl}
            deleteUrl={deleteEulaFileUrl}
            defaultNow={defaultNow}
            fileUploadButtonText={fileUploadButtonText}
            fileFormatIncorrectText={
              'Failed to upload the file. Format is incorrect.'
            }
            fileUploadContainerTitle={fileUploadContainerTitle}
            isMultiFileUpload={isMultiFileUpload}
            updateErr={updateErr}
            uploadError={uploadError}
            bucketName={bucketName}
            checkDuplicate={true}
            showRealFileName={true}
            disabled={disableEulaFileUpload}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}
